import React, { useState } from "react";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Monitor, Gamepad, Smartphone, Zap, Wifi, Activity, Bug, CheckCircle2 } from "lucide-react";

export default function FpsFixPage() {
  const { t } = useT();
  const PLATFORMS = [
    { value: "pc", label: t("page.fps.pc"), icon: Monitor },
    { value: "console", label: t("page.fps.console"), icon: Gamepad },
    { value: "mobile", label: t("page.fps.mobile"), icon: Smartphone },
  ];
  const ISSUES = [
    { value: "fps", label: t("page.fps.low_fps"), icon: Activity },
    { value: "lag", label: t("page.fps.lag"), icon: Zap },
    { value: "ping", label: t("page.fps.ping"), icon: Wifi },
    { value: "crash", label: t("page.fps.crash"), icon: Bug },
  ];

  const [step, setStep] = useState(0);
  const [platform, setPlatform] = useState(null);
  const [issue, setIssue] = useState(null);
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);

  const runWizard = async () => {
    setLoading(true);
    try {
      const res = await api.post("/fps-fix", { platform, issue });
      setTips(res.data.items);
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep(0);
    setPlatform(null);
    setIssue(null);
    setTips([]);
  };

  return (
    <AppShell>
      <PageContainer>
        <PageHeader
          kicker={t("page.fps.kicker")}
          title={t("page.fps.title")}
          subtitle={t("page.fps.sub")}
        />

        {/* Progress */}
        <div className="flex items-center gap-3 mb-6">
          {[0, 1, 2].map((i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-neon" : "bg-white/10"}`} />
          ))}
        </div>

        {step === 0 && (
          <section data-testid="fps-step-platform">
            <h2 className="font-display text-2xl font-bold mb-1">{t("page.fps.what_platform")}</h2>
            <p className="text-zinc-400 mb-5">{t("page.fps.what_platform_sub")}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {PLATFORMS.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.value}
                    onClick={() => {
                      setPlatform(p.value);
                      setStep(1);
                    }}
                    data-testid={`fps-platform-${p.value}`}
                    className="card-interactive rounded-2xl border border-white/10 bg-zinc-900/60 p-6 text-left"
                  >
                    <Icon className="text-neon" size={28} />
                    <div className="mt-3 font-display text-xl font-bold">{p.label}</div>
                    <div className="text-sm text-zinc-500 mt-1">Tailored to {p.label.toLowerCase()} gamers.</div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === 1 && (
          <section data-testid="fps-step-issue">
            <h2 className="font-display text-2xl font-bold mb-1">{t("page.fps.whats_happening")}</h2>
            <p className="text-zinc-400 mb-5">{t("page.fps.whats_happening_sub")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {ISSUES.map((s) => {
                const Icon = s.icon;
                return (
                  <button
                    key={s.value}
                    onClick={() => setIssue(s.value)}
                    data-testid={`fps-issue-${s.value}`}
                    className={`card-interactive rounded-2xl border p-5 text-left ${
                      issue === s.value ? "border-neon bg-neon/10" : "border-white/10 bg-zinc-900/60"
                    }`}
                  >
                    <Icon className={`${issue === s.value ? "text-neon" : "text-zinc-400"}`} size={24} />
                    <div className="mt-3 font-bold">{s.label}</div>
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)} data-testid="fps-back">
                {t("onb.back")}
              </Button>
              <Button
                disabled={!issue || loading}
                onClick={runWizard}
                className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
                data-testid="fps-run"
              >
                {loading ? t("page.fps.analyzing") : t("page.fps.get_fixes")}
              </Button>
            </div>
          </section>
        )}

        {step === 2 && (
          <section data-testid="fps-step-results">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="font-display text-2xl font-bold">{t("page.fps.fix_plan")}</h2>
                <p className="text-zinc-400 text-sm">
                  {t("page.fps.platform_label")} <span className="text-neon">{platform}</span> · {t("page.fps.issue_label")}{" "}
                  <span className="text-neon">{issue}</span>
                </p>
              </div>
              <Button variant="outline" className="rounded-full border-neon/40 text-neon bg-transparent hover:bg-neon/10 hover:text-neon" onClick={reset} data-testid="fps-reset">
                {t("page.fps.start_over")}
              </Button>
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {tips.map((tip, i) => (
                <li
                  key={tip.id}
                  className="rounded-2xl border border-white/5 bg-zinc-900/60 p-5 animate-fade-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                  data-testid={`fps-tip-${tip.id}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-neon/10 border border-neon/20 text-neon grid place-items-center font-mono text-xs font-bold shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-display font-bold">{tip.title}</div>
                      <p className="text-sm text-zinc-400 mt-1">{tip.description}</p>
                      <div className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-neon">
                        <CheckCircle2 size={12} /> {tip.category}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </PageContainer>
    </AppShell>
  );
}
